import { DataType } from "@/types/enums/DataType";
import { Field } from "@/types/Field";
import ALLOWED_GROUP_BY_COLUMNS from "./groupByGuestColumns.generated.json";

/**
 * Row grouping for the guest list — the cockpit twin of the export popup's
 * `groupByMainGuest` / `gridGroupBy` pair.
 *
 * The list carries ONE value where the export wire carries two, because the
 * user has always been choosing one thing. The two modes stay mutually
 * exclusive by construction: a value either IS the party sentinel, or it is a
 * column key.
 *
 * Mirror of main-backend `GuestGroupBy`. A key offered here and refused there
 * is a 400 the user cannot act on, so the two lists are meant to be read
 * side by side.
 */

/** No grouping — the flat person list that shipped before this feature. */
export const NO_GROUP_KEY = "";

/**
 * One row per party (main guest), companions nested underneath. Not a field
 * name: `mainGuest` as a FIELD holds the parent's id, which is exactly what a
 * party is keyed by, and reusing the name keeps the wire honest about that.
 */
export const PARTY_GROUP_KEY = "mainGuest";

export function isPartyGroup(groupBy: string | undefined | null): boolean {
  return groupBy === PARTY_GROUP_KEY;
}

export function isColumnGroup(groupBy: string | undefined | null): boolean {
  return !!groupBy && groupBy !== PARTY_GROUP_KEY;
}

/**
 * May the list group by this column?
 *
 * A NAME rule, read from `groupByGuestColumns.generated.json`, which is
 * generated out of main-backend's `GuestGroupBy` constants
 * (`scripts/gen-groupby-guest-columns.mjs`).
 *
 * It used to be a TYPE rule borrowed from the export popup
 * (`isDataTypeGroupable`), and that is the whole reason this file now has a
 * generator behind it: the backend has always decided groupability by name, so
 * the two rules drifted apart on 13 of the 20 columns the menu offered —
 * `origin`, `emailOptIn`, `lastEmailStatus`, `seatStatuses`, every `*Ids`
 * rollup. Each answered 400, the cockpit swallowed it, the choice was
 * persisted, and the next page load restored it into a full-page error with no
 * way back. The drift also ran the other way: the backend accepted
 * `acceptanceState`, which the picker never offered.
 *
 * A type rule and a name rule cannot be kept in agreement by intention, so the
 * cockpit no longer holds an opinion — it reads the backend's.
 *
 * Custom fields are allowed by RULE, not by name: the backend accepts any
 * `additionalFields.<key>` because the event owner defined it.
 */
export function isListGroupable(field: Field): boolean {
  if (field.additionalField) {
    // The ONE thing the cockpit still hides on its own. An IMAGE custom field
    // stores an object (storage key + metadata), so every guest is its own
    // group: the header count caps out, and the table draws one header per row.
    // The backend would accept it — hiding it is noise control, not a rule.
    //
    // Hiding something the backend ACCEPTS is the safe direction: it can only
    // cost an option, never produce a 400. Offering something the backend
    // REFUSES is the direction that broke the feature, and the generated list
    // above closes it.
    return field.dataType !== DataType.IMAGE;
  }
  return (ALLOWED_GROUP_BY_COLUMNS as readonly string[]).includes(field.name);
}

/**
 * Why the backend refused the active grouping.
 *
 * Both values map to a real message: the cockpit has to SAY something here,
 * because the alternative — what shipped first — was a menu pick that appeared
 * to do nothing while quietly persisting itself.
 */
export type GroupByRejectionReason = "groupTooLarge" | "groupByNotAllowed";

/**
 * The rejection as the UI carries it: a reason plus a SEQUENCE NUMBER.
 *
 * `null` while nothing is wrong.
 *
 * <p>The number is not decoration. The reason alone was a bare string, and the
 * toast that announces it hangs on the state's identity — so a SECOND rejection
 * carrying the same reason wrote the same value, React bailed out of the
 * update, the effect's deps never changed, and nothing was said. The control
 * snapped back to "(None)" in silence on exactly the retry the first toast
 * invited. Bumping `seq` gives every refusal its own identity.
 */
export type GroupByRejection = { reason: GroupByRejectionReason; seq: number } | null;

/** Next rejection state — a fresh identity even when the reason repeats. */
export function nextGroupByRejection(
  previous: GroupByRejection,
  reason: GroupByRejectionReason,
): GroupByRejection {
  return { reason, seq: (previous?.seq ?? 0) + 1 };
}

/**
 * Is this refusal expected to answer differently on a later try?
 *
 * `groupTooLarge` is TRANSIENT: the cap is compared against the anchors the
 * FULL list predicate resolves — filters, search, `archived`, whitelist, the
 * conditions tree — so the same event crosses it in both directions as guests
 * are imported, archived or filtered. Discarding the user's stored grouping on
 * one such answer throws away a setting that would work again a minute later.
 *
 * `groupByNotAllowed` is permanent: the column is not groupable for this
 * caller, and it will 400 identically forever.
 */
export function isTransientGroupByRejection(reason: GroupByRejectionReason): boolean {
  return reason === "groupTooLarge";
}

/**
 * Does this server message mean "your grouping is not acceptable"?
 *
 * Matched on the message rather than a code because the backend answers these
 * through `ErrorFormater`'s validation channel, which carries the offending key
 * in the text (`validationError: groupBy 'x' not allowed for guests`). The one
 * fixed code is the party-set cap.
 *
 * The point of recognising it at all: the generic "keep the rows and log"
 * handling leaves the user with a list that silently ignored their pick — and a
 * stored preference that turns the next page load into a dead end. Whether the
 * refusal will repeat is a SEPARATE question, answered by
 * {@link isTransientGroupByRejection}; the two used to be conflated, and the
 * recovery threw the preference away on the transient one.
 *
 * <p><b>What `message` actually holds.</b> Not the backend's sentence. Every
 * 400 is resolved to an `errorCode` by `ErrorFormater`, and `handleErrorServer`
 * puts THAT in `Error.message` while the prose goes to `.detail`. So matching
 * prose alone matched nothing in production: a permanent refusal arrived as the
 * bare `"validationError"` and fell through to the generic branch, leaving the
 * dead end this whole path exists to remove. Only `groupTooLarge` ever worked,
 * because it survives ErrorFormater's camelCase pass-through unchanged.
 *
 * <p>Three shapes are accepted, and the prose ones stay accepted for good:
 *
 * <ol>
 *   <li>`groupByNotAllowed` / `groupByNotAllowed:&lt;key&gt;` — the code the
 *       backend mints, mirroring `unknownFilterKey:` right beside it.</li>
 *   <li>`groupTooLarge` — the party-set cap.</li>
 *   <li>the raw sentences — what a cockpit deployed AHEAD of its backend sees,
 *       or a cached bundle that outlived a backend deploy. Keeping them makes
 *       the recovery independent of deploy order, which matters because
 *       "a client that outlived a backend change" is one of the two ways to
 *       reach the refusal at all.</li>
 * </ol>
 *
 * <p>The prose arm needs the trailing space: `groupBy ` and not `groupBy`.
 * The export lane validates neighbouring parameters and says
 * `validationError: groupByMainGuest …` and `validationError: gridGroupBy …`,
 * neither of which is this list's grouping — a substring test swallows both and
 * silently drops the user's grouping on an unrelated export failure.
 */
export function isGroupByRejection(message: string): boolean {
  return (
    message === "groupByNotAllowed"
    || message.startsWith("groupByNotAllowed:")
    || message.startsWith("groupTooLarge")
    || message.startsWith("validationError: groupBy ")
    || message.startsWith("groupBy ")
  );
}

/**
 * Where a REFUSED grouping is parked for the rest of this tab's session.
 *
 * <p>The party cap is transient — it is measured against the anchors the full
 * list predicate resolves, so an import, an archive or a filter change moves an
 * event across it in both directions. Deleting the user's stored grouping on
 * one such answer throws away a setting that works again a minute later, which
 * is why the preference survives it.
 *
 * <p>But "survives" without a brake means every page load of a permanently
 * over-cap event pays for it: a grouped row request and a grouped count
 * request, both 400, both resolving up to the anchor cap, plus a skeleton frame
 * and a toast — forever, with no way for the user to stop it (the picker reads
 * "(None)", so there is nothing to clear). The session marker turns that into
 * ONE attempt per tab. A new tab, or deliberately picking the grouping again,
 * retries.
 *
 * <p>`sessionStorage`, not `localStorage`: the whole point is that it dies with
 * the tab. It also needs no entry in `accountScopedState` — that sweep works
 * off an inverted keep-list, so a new key is account-bound by default and is
 * cleared on logout.
 */
export function groupBySuspendedKey(eventId: string): string {
  return `groupBySuspended-guest-${eventId}`;
}

/** Park the refused grouping for this tab. Never throws — private mode, quota. */
export function suspendGroupByForSession(eventId: string): void {
  try {
    window.sessionStorage.setItem(groupBySuspendedKey(eventId), "1");
  } catch {
    // No session storage: the cost is a retry per page load, not a defect.
  }
}

/** Is the stored grouping parked for this tab? */
export function isGroupBySuspendedForSession(eventId: string): boolean {
  try {
    return window.sessionStorage.getItem(groupBySuspendedKey(eventId)) === "1";
  } catch {
    return false;
  }
}

/** Deliberately asking for a grouping again clears the park. */
export function clearGroupBySuspension(eventId: string): void {
  try {
    window.sessionStorage.removeItem(groupBySuspendedKey(eventId));
  } catch {
    /* nothing to clear */
  }
}

/**
 * Where the guest list's row grouping is remembered, per event.
 *
 * One spelling, because there are two readers and they used to hold a copy
 * each: `MetaTableProvider` writes it (generically, as
 * `groupBy-${prefScope()}`) and the guest-list page reads it back before the
 * provider exists. A silent divergence would not break anything loudly — the
 * grouping would simply never be restored — so it is pinned by a test rather
 * than trusted.
 */
export function groupByPrefKey(eventId: string): string {
  return `groupBy-guest-${eventId}`;
}
